/**
 * PostHog Analytics Utilities
 *
 * This module provides type-safe wrappers for PostHog analytics.
 *
 * IMPORTANT: For HIPAA Compliance
 * - NEVER track PHI (Protected Health Information)
 * - NEVER include patient names, MRNs, diagnoses, addresses, phone, email
 *   (outside `identify`), free-text notes, search terms (use length only).
 * - All event properties are filtered through `sanitizeProperties` against
 *   `PHI_DENYLIST` before transmission. The denylist is enforced in
 *   `lib/posthog-events.ts`.
 * - When in doubt, don't track it.
 */

import posthog from 'posthog-js';
import {
  type AppEventName,
  type AppEventProps,
  sanitizeProperties,
} from './posthog-events';

/**
 * Check if PostHog is available and initialized
 */
export function isPostHogAvailable(): boolean {
  return typeof window !== 'undefined' && posthog.__loaded;
}

/**
 * Type-safe event capture for events listed in `AppEventMap`.
 * Properties are sanitized against the PHI denylist before transmission.
 */
export function trackAppEvent<E extends AppEventName>(
  eventName: E,
  properties?: AppEventProps<E>,
): void {
  if (!isPostHogAvailable()) return;
  const safe = sanitizeProperties(
    eventName,
    properties as Record<string, unknown> | undefined,
  );
  posthog.capture(eventName, safe);
}

/**
 * Untyped event capture, retained for legacy call sites and one-off events.
 *
 * Prefer `trackAppEvent` for any new instrumentation so the event name and
 * property shape are checked at compile time.
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
) {
  if (!isPostHogAvailable()) return;
  const safe = sanitizeProperties(eventName, properties);
  posthog.capture(eventName, safe);
}

/**
 * Track page view (usually handled automatically by provider)
 */
export function trackPageView(url?: string) {
  if (isPostHogAvailable()) {
    posthog.capture('$pageview', {
      $current_url: url || window.location.href,
    });
  }
}

/**
 * Identify a user
 *
 * @param userId - Unique user identifier (NO PHI!)
 * @param properties - User properties (NO PHI!)
 */
export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>,
) {
  if (isPostHogAvailable()) {
    posthog.identify(userId, properties);
  }
}

/**
 * Reset user identity (on logout)
 */
export function resetUser() {
  if (isPostHogAvailable()) {
    posthog.reset();
  }
}

/**
 * Track feature flag evaluation
 */
export function trackFeatureFlag(flagKey: string, flagValue: boolean | string) {
  trackAppEvent('feature_flag_evaluated', {
    flag_key: flagKey,
    flag_value: flagValue,
  });
}

/**
 * Get feature flag value
 */
export function getFeatureFlag(flagKey: string): boolean | string | undefined {
  if (isPostHogAvailable()) {
    return posthog.getFeatureFlag(flagKey);
  }
  return undefined;
}

/**
 * Check if feature is enabled
 */
export function isFeatureEnabled(flagKey: string): boolean {
  if (isPostHogAvailable()) {
    return posthog.isFeatureEnabled(flagKey) ?? false;
  }
  return false;
}

// Common event tracking functions for the app

export function trackButtonClick(buttonName: string, location?: string) {
  trackEvent('button_clicked', {
    button_name: buttonName,
    location: location || window.location.pathname,
  });
}

export function trackModalOpen(modalName: string) {
  trackEvent('modal_opened', {
    modal_name: modalName,
  });
}

export function trackModalClose(modalName: string) {
  trackEvent('modal_closed', {
    modal_name: modalName,
  });
}

export function trackFormSubmit(formName: string, success: boolean) {
  trackEvent('form_submitted', {
    form_name: formName,
    success,
  });
}

export function trackError(errorType: string, errorMessage?: string) {
  trackEvent('error_occurred', {
    error_message: errorMessage,
    error_type: errorType,
    page: window.location.pathname,
  });
}

export function trackSearch(searchTerm: string, resultCount: number) {
  trackEvent('search_performed', {
    result_count: resultCount,
    search_term_length: searchTerm.length,
  });
}

export function trackTableAction(action: string, tableName: string) {
  trackEvent('table_action', {
    action,
    table_name: tableName,
  });
}

export function trackNavigation(from: string, to: string) {
  trackEvent('navigation', {
    from,
    to,
  });
}

export function trackAPICall(
  endpoint: string,
  method: string,
  statusCode: number,
  duration: number,
) {
  trackEvent('api_call', {
    duration_ms: duration,
    endpoint: endpoint.replace(/\/\d+/g, '/:id'),
    method,
    status_code: statusCode,
  });
}

/**
 * Associate the current person with a Group (PostHog Groups Analytics).
 * Used for practice/tenant-level cohorts and funnels.
 */
export function setGroup(
  groupType: 'practice' | 'tenant',
  groupKey: string,
  properties?: Record<string, unknown>,
) {
  if (isPostHogAvailable() && groupKey) {
    posthog.group(groupType, groupKey, properties);
  }
}

export default posthog;
